import { useStartEvaluation } from '../Evaluations/useStartEvaluation';

import {
    GROWTH_GLASS_SURFACE,
    GROWTH_SECTION_PADDING_X,
} from '@/components/CareerGrowth/careerGrowthStyles';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { PastIllustration } from '@/components/Illustrations/Illustration';
import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { CycleInstanceSteps } from '@/constants/cycle-evaluation/CycleInstanceSteps';
import { useAmelioTranslations, useNavigate } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { RouteName } from '@/navigation/types';

/** The tab's own breathing room, as the refused Croissance tab next door draws it. */
const NOTICE_PADDING = `${GROWTH_SECTION_PADDING_X} py-12`;

/** The size every illustration of the module is drawn at; the width follows its own ratio. */
const ILLUSTRATION_SIZE = 'h-32 w-auto';

interface AssessmentForbiddenNoticeProps {
    employeeId: string;
    /** Whether the reader may open an evaluation at all; without it the self-assessment is not offered. */
    canStartEvaluations: boolean;
    dataTestId?: string;
}

/**
 * What the "Évaluation" tab shows to a reader looking at their own profile. Nobody reads their own
 * results, whatever their role: this tab is the manager's working view, and the employee is given
 * the same figures in their portal. A rule rather than a failure, so it is drawn like the refused
 * Croissance tab next door and never in the error colour.
 *
 * The self-assessment stays reachable from here. It is the one evaluation a reader fills in about
 * themselves, and this tab is its only entry point.
 */
export const AssessmentForbiddenNotice = ({
    employeeId,
    canStartEvaluations,
    dataTestId = 'assessment-tab-forbidden',
}: AssessmentForbiddenNoticeProps) => {
    const { t } = useAmelioTranslations();
    const { getManagerLabel } = useCompanyInformation();
    const navigate = useNavigate();
    const { start: startSelfEvaluation, isCreating } = useStartEvaluation({
        evaluationType: EvaluationType.SELF_EVALUATION,
        firstStep: CycleInstanceSteps.SELECT_TEMPLATE,
    });

    return (
        <section data-testid={dataTestId}>
            <EmptyState
                className={`${GROWTH_GLASS_SURFACE} ${NOTICE_PADDING}`}
                illustration={<PastIllustration className={ILLUSTRATION_SIZE} />}
                title={t('performanceProfile.assessment.forbidden.title')}
                message={t('performanceProfile.assessment.forbidden.description', {
                    managerLabel: getManagerLabel(),
                })}
                secondaryAction={
                    canStartEvaluations
                        ? {
                              label: t('performanceProfile.evaluations.self.startButton'),
                              onClick: () => startSelfEvaluation(employeeId),
                              disabled: isCreating,
                          }
                        : undefined
                }
                action={{
                    label: t('performanceProfile.assessment.forbidden.viewPortal'),
                    onClick: () => navigate({ path: RouteName.EmployeePortal }),
                }}
            />
        </section>
    );
};
